import { Player, Video } from '@loomhq/createhq/dist/video-player';
import Hls from 'hls.js';
import mux from 'mux-embed';

import { MUX_ENV_KEY } from 'constants/videoPlaybackStats';
import { isMobile } from 'utilities/device';

declare global {
  interface Window {
    initShakaPlayerMux: (
      player: HTMLVideoElement,
      config: MuxMonitorConfig
    ) => void;
  }
}

export function setupHlsMonitor(
  plainSourceUrl: string,
  player: Player,
  userAnonId: string,
  videoModel: Video
): void {
  const muxConfig = getMuxMonitorConfig({
    plainSourceUrl,
    userAnonId,
    videoModel,
  });

  muxConfig.Hls = Hls;
  muxConfig.hlsjs = player.mseTech;

  mux.monitor(player.media, muxConfig);
}

export function setupDashMonitor(
  plainSourceUrl: string,
  videoElement: HTMLVideoElement,
  userAnonId: string,
  videoModel: Video
): void {
  // Add stats report based on feature flag
  if (!videoElement || !window.initShakaPlayerMux) {
    return;
  }

  return window.initShakaPlayerMux(
    videoElement,
    getMuxMonitorConfig({ plainSourceUrl, userAnonId, videoModel })
  );
}

type muxConfigProps = {
  plainSourceUrl: string;
  userAnonId: string;
  videoModel?: Video;
};

type MuxMonitorConfig = {
  debug: false;
  Hls: undefined | Hls;
  hlsjs: undefined | unknown;
  data: {
    env_key: string;
    video_id: string | null;
    video_title: string | null;
    viewer_user_id: string;
    player_name: string;
    player_version: string;
    player_software: string;
    sub_property_id: string | null;
    experiment_name: string | null;
    video_encoding_variant: string | null;
  };
};

function getMuxMonitorConfig({
  plainSourceUrl,
  userAnonId,
  videoModel,
}: muxConfigProps): MuxMonitorConfig {
  const experimentName = videoModel?.processingInformation
    ?.instantEditingEnabled
    ? 'avserver'
    : null;

  return {
    debug: false,
    Hls: undefined,
    hlsjs: undefined,
    data: {
      env_key: MUX_ENV_KEY,
      video_id: videoModel?.id ?? null,
      video_title: videoModel?.id ?? null,
      viewer_user_id: userAnonId,
      player_name: isMobile ? 'Native' : 'Loom',
      player_version: 'fresh',
      player_software: 'createhq',
      sub_property_id: getSubProp(plainSourceUrl),
      experiment_name: experimentName,
      // @ts-expect-error ignore due to enabling strict null checks
      video_encoding_variant: getEncodingVariant(videoModel?.videoProperties),
    },
  };
}

const getEncodingVariant = (props: Video['videoProperties']): string | null => {
  if (!props) {
    return null;
  }

  const os = props.os || '';
  const os_underscored = os.toLowerCase().replace(/ /g, '_');

  return [os_underscored, props.recordingVersion].filter(Boolean).join('_');
};

const sourceTypeFromRegex = {
  'hls raw': /sessions\/raw\/.*?\.m3u8/,
  'hls transcoded': /sessions\/transcoded\/.*?\.m3u8/,
  'hls jit': /sessions\/hls\/.*?\.m3u8/,
  'dash raw': /sessions\/raw\/.*?\.mpd/,
  'mp4 transcoded': /sessions\/transcoded\/.*?\.mp4/,
};

const getSubProp = (url: string): string | null => {
  for (const [sourceType, regex] of Object.entries(sourceTypeFromRegex)) {
    if (url?.match(regex)) {
      return sourceType;
    }
  }

  return null;
};
